import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { fetchAllSingles, type RankingItem } from "../utils/homeApi";

type RankedItem = RankingItem & { position: number };

const PlayerSummaries: React.FC = () => {
  const [playerRatings, setPlayerRatings] = useState<RankingItem[]>([]);
  const [filteredPlayers, setFilteredPlayers] = useState<RankedItem[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<"rating" | "games" | "winRate">("rating");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const playersPerPage = 20;

  useEffect(() => {
    const loadData = async () => {
      try {
        // Fetch the full list — projection table is small enough to ship at once
        // (one row per player, no match history). Sorting/filter happen client-side.
        const res = await fetchAllSingles({ limit: 500 });
        let acc: RankingItem[] = res.items;
        let offset = res.items.length;
        while (offset < res.total) {
          const next = await fetchAllSingles({ limit: 500, offset });
          acc = acc.concat(next.items);
          offset += next.items.length;
          if (next.items.length === 0) {
            break;
          }
        }
        setPlayerRatings(acc);
      } catch (error) {
        console.error("Error loading player ratings:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    const sorted = [...playerRatings].sort((a, b) => {
      let aValue: number;
      let bValue: number;

      switch (sortBy) {
        case "rating":
          aValue = a.rating;
          bValue = b.rating;
          break;
        case "games":
          aValue = a.gamesPlayed;
          bValue = b.gamesPlayed;
          break;
        case "winRate":
          aValue = a.gamesPlayed > 0 ? (a.gamesWon / a.gamesPlayed) * 100 : 0;
          bValue = b.gamesPlayed > 0 ? (b.gamesWon / b.gamesPlayed) * 100 : 0;
          break;
        default:
          aValue = a.rating;
          bValue = b.rating;
      }

      if (sortOrder === "asc") {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });

    // Position comes from the full list so searching never renumbers rows.
    const ranked = sorted.map((item, index) => ({ ...item, position: index + 1 }));
    const normalizedSearch = searchTerm.toLowerCase();
    const filtered = ranked.filter((player) =>
      player.name.toLowerCase().includes(normalizedSearch),
    );

    setFilteredPlayers(filtered);
    setCurrentPage(1);
  }, [playerRatings, searchTerm, sortBy, sortOrder]);

  const totalPages = Math.ceil(filteredPlayers.length / playersPerPage);
  const startIndex = (currentPage - 1) * playersPerPage;
  const endIndex = startIndex + playersPerPage;
  const currentPlayers = filteredPlayers.slice(startIndex, endIndex);

  const getRatingBadgeClass = (rating: number): string => {
    if (rating < 1600) {
      return "low";
    }

    if (rating < 1800) {
      return "medium";
    }

    if (rating < 2000) {
      return "high";
    }

    return "very-high";
  };

  if (loading) {
    return (
      <div className="card">
        <h2>Wczytywanie…</h2>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <h2>Ranking singlowy</h2>
        <p>Liczba zawodników: {filteredPlayers.length}</p>
      </div>

      <div className="search-container">
        <input
          type="text"
          name="player-filter"
          autoComplete="off"
          spellCheck={false}
          data-1p-ignore
          data-lpignore="true"
          placeholder="Szukaj zawodników…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="card">
        <div className="sort-controls">
          <label className="sort-controls__field">
            <span className="sort-controls__label">Sortuj według</span>
            <select
              value={sortBy}
              onChange={(e) =>
                setSortBy(e.target.value as "rating" | "games" | "winRate")
              }
              className="sort-controls__select"
            >
              <option value="rating">Rating</option>
              <option value="games">Rozegrane mecze</option>
              <option value="winRate">Skuteczność</option>
            </select>
          </label>
          <button
            onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
            className="btn-primary"
          >
            {sortOrder === "asc" ? "↑ Rosnąco" : "↓ Malejąco"}
          </button>
        </div>

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Miejsce</th>
                <th>Zawodnik</th>
                <th>Rating</th>
                <th>Rozegrane mecze</th>
                <th>Wygrane</th>
                <th>Przegrane</th>
                <th>Remisy</th>
                <th title="Procent wygranych spotkań. Najedź na wartość żeby zobaczyć remisy/przegrane.">% Wygranych</th>
                <th title="Zmiana ratingu w ostatnim turnieju">Δ ostatni turniej</th>
                <th>Turnieje</th>
              </tr>
            </thead>
            <tbody>
              {currentPlayers.map((player, index) => (
                <tr key={player.playerId}>
                  <td>{player.position}</td>
                  <td>
                    <Link to={`/player/${player.playerId}`} className="player-link">
                      {player.name}
                    </Link>
                  </td>
                  <td>
                    <span
                      className={`rating-badge ${getRatingBadgeClass(player.rating)}`}
                    >
                      {player.rating}
                    </span>
                  </td>
                  <td>{player.gamesPlayed}</td>
                  <td>{player.gamesWon}</td>
                  <td>{player.gamesLost}</td>
                  <td>{player.gamesDrawn}</td>
                  <td title={player.gamesPlayed > 0
                    ? `Wygrane ${((player.gamesWon / player.gamesPlayed) * 100).toFixed(1)}% · Remisy ${((player.gamesDrawn / player.gamesPlayed) * 100).toFixed(1)}% · Przegrane ${((player.gamesLost / player.gamesPlayed) * 100).toFixed(1)}%`
                    : 'Brak meczów'}>
                    {player.gamesPlayed > 0
                      ? `${((player.gamesWon / player.gamesPlayed) * 100).toFixed(1)}%`
                      : '—'}
                  </td>
                  <td
                    style={{
                      color: player.ratingDifference >= 0 ? "green" : "red",
                    }}
                  >
                    {player.ratingDifference >= 0 ? "+" : ""}
                    {player.ratingDifference}
                  </td>
                  <td>{player.tournamentsPlayed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="btn-secondary"
              style={{ marginRight: "1rem" }}
            >
              Poprzednia
            </button>
            <span style={{ margin: "0 1rem" }}>
              Strona {currentPage} z {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="btn-secondary"
              style={{ marginLeft: "1rem" }}
            >
              Następna
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerSummaries;
